import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { initialsFor } from '../lib/glyph'

/**
 * Supabase Auth, built as the permanent layer rather than scaffolding.
 *
 * Magic-link email is the method today and Azure is the method later; switching
 * is `signInWithOAuth({ provider: 'azure' })` in one place below, and tenant
 * restriction lives in the Entra app registration, not in this code. Nothing
 * else in the app knows which method signed the user in, and nothing
 * server-side changes between them, because the RLS policies and grants are
 * written against the `authenticated` role rather than against named users.
 */

interface AuthValue {
  session: Session | null
  user: User | null
  /** Ready is false only during the first session restore, so guards do not flash. */
  ready: boolean
  email: string | null
  displayName: string | null
  initials: string
  sendMagicLink: (email: string) => Promise<void>
  signInWithMicrosoft: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let active = true
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      setSession(data.session)
      setReady(true)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setReady(true)
    })
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthValue>(() => {
    const user = session?.user ?? null
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>
    const displayName =
      (typeof meta['full_name'] === 'string' && meta['full_name']) ||
      (typeof meta['name'] === 'string' && meta['name']) ||
      null
    const email = user?.email ?? null
    return {
      session,
      user,
      ready,
      email,
      displayName,
      initials: initialsFor(displayName, email),
      async sendMagicLink(address: string) {
        const { error } = await supabase.auth.signInWithOtp({
          email: address,
          options: { emailRedirectTo: window.location.origin },
        })
        if (error) throw new Error(error.message)
      },
      async signInWithMicrosoft() {
        // The switch, when the Entra app registration is ready. Enabling the
        // Azure provider in Supabase and deleting the magic-link block in
        // SignIn is the whole change.
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'azure',
          options: { scopes: 'email openid profile', redirectTo: window.location.origin },
        })
        if (error) throw new Error(error.message)
      },
      async signOut() {
        await supabase.auth.signOut()
      },
    }
  }, [session, ready])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
